/**
 * The `operations` section — RO-3-06 (task #44299).
 *
 * RO-3-01's section map scopes this item to NINE pages
 * (`docs/rollover/inventory/pages.md` § Operations). On this tree the shared
 * render layer (RO-3-00) already ports ModePage / ModeSectionRoute / domain
 * mounts for finance.ledger + team.directory, so most of the section is
 * "wire ModePage + one new dashboard READ + close three gaps".
 *
 * ══ What becomes a route here ═══════════════════════════════════════════════
 *
 * 1. `/operations` — ModePage + OperationsDashboard in `dashboardSlot` on the
 *    bare dashboard view (exact Next semantics from `app/operations/page.tsx`).
 * 2. `/operations/$` — ModeSectionRoute for `/operations/<section>[/<view>]`
 *    (replaces the RO-3-14 pending placeholder). Empty-splat handoff paints the
 *    mode root so the catch-all cannot steal bare `/operations` (#44581 class).
 * 3. `/directory/$kind/$id` — resolve numeric id → uid via `directory.resolve`,
 *    then redirect to `/o/<uid>?from=operations` (replaces RO-3-14 pending).
 *
 * ══ What deliberately does NOT become a route ═══════════════════════════════
 *
 * `/finance`, `/team`, `/directory`, `/audits` — CORE_ROUTE folds, already
 * handled by `shell/redirects.ts` gate 8 (same hiring discipline, RO-3-07).
 * Measured on this tree:
 *
 *     /finance    → /operations?area=finance
 *     /team       → /operations?area=team
 *     /directory  → /operations?area=team
 *     /audits     → /operations?area=audits
 *
 * Registering a route for any of them would be unreachable code (cascade fires
 * first) and would make the route a liar about query preservation. The audits
 * CONTENT lives under ModePage as `ops.audits` → `AuditsHub` (domain-mounts).
 *
 * `/operations/o/$uid` — already ported by RO-3-14 as a pure redirect.
 * `/integrations/connected` — owned by #44577 (`singletons.tsx`); do not
 * re-register here (duplicate path blanks the tree — RO-10-08 / #44583 class).
 *
 * ══ Transport changes forced by the port ════════════════════════════════════
 *
 * - OperationsDashboard server reads → `operations.dashboard` (GET, READ)
 * - AuditsHub list + self-heal → `sectionAudit.hub` (GET, READ)
 * - directory entity id→uid SELECT → `directory.resolve` (GET, READ)
 * - Run-all reuses `otherActions.auditRunDue` (already mounted, RO-2-11b)
 */

import * as React from "react";
import { createRoute, redirect } from "@tanstack/react-router";
import { PageHeader, objectHref } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { useNavigate, useSearch } from "@excellent/ui/router";
import { directory } from "@excellent/ui/api/contracts/directory";
import { ModePage, ModeSectionRoute, redirectAreaToPath } from "../shared";
import { OperationsDashboard } from "../shared/operations/dashboard";
import { MODE_VIEWS, type ModeView } from "@/modes/modes-registry";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";

// ── /operations — mode root ─────────────────────────────────────────────────

function OperationsIndexPage() {
  const navigate = useNavigate();
  // Seam useSearch (URLSearchParams), not operationsIndexRoute.useSearch():
  // RO-10-08 empty-splat handoff renders this from operationsSectionsRoute, and
  // a route-scoped useSearch throws "Invariant failed" when that route is not
  // the match (#44594 — #44299 shipped the page dead for this reason).
  const spParams = useSearch();
  const view = spParams.get("view") ?? undefined;
  const area = spParams.get("area") ?? undefined;
  const sub = spParams.get("sub") ?? undefined;
  const search: Record<string, string | undefined> = {
    ...(view ? { view } : {}),
    ...(area ? { area } : {}),
    ...(sub ? { sub } : {}),
  };

  // Issue 3 flip: legacy `?area=` redirects to the canonical section path.
  React.useEffect(() => {
    redirectAreaToPath("operations", search, navigate);
  }, [view, area, sub, navigate]);

  const v = (MODE_VIEWS as readonly string[]).includes(view ?? "")
    ? (view as ModeView)
    : "dashboard";

  // Bare mode root + dashboard view gets the question-first dashboard slot.
  const isBareDashboard = !area && v === "dashboard";
  const dashboardSlot = isBareDashboard ? <OperationsDashboard /> : undefined;

  return (
    <ModePage
      mode="operations"
      view={v}
      area={area}
      sub={sub}
      searchParams={search}
      dashboardSlot={dashboardSlot}
    />
  );
}

export const operationsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations",
  component: OperationsIndexPage,
});

// ── /operations/$ — section catch-all ───────────────────────────────────────

function OperationsSectionsPage() {
  const params = operationsSectionsRoute.useParams();
  const search = operationsSectionsRoute.useSearch() as Record<
    string,
    string | string[] | undefined
  >;
  // TanStack splat is a string; Next's segments were string[].
  const splat =
    (params as { _splat?: string })._splat ??
    (params as { "*"?: string })["*"] ??
    "";
  // Empty splat can steal bare `/operations` from operationsIndexRoute —
  // hand off so the mode root + dashboard still paint (same pattern as product).
  if (!splat) return <OperationsIndexPage />;
  const segments = splat.split("/").filter(Boolean);
  return (
    <ModeSectionRoute
      mode="operations"
      segments={segments}
      searchParams={search}
    />
  );
}

export const operationsSectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/$",
  // #44601: empty splat outranks bare `/operations` — reject so the index wins
  // the match (render hand-off above stays as belt-and-suspenders).
  params: {
    parse: (raw) => {
      if (!raw._splat) return false;
      return raw;
    },
  },
  validateSearch: routeSearchSchema({}),
  component: OperationsSectionsPage,
});

// ── /directory/$kind/$id — id→uid then redirect ─────────────────────────────

const KINDS = new Set(["person", "organization"]);

function DirectoryEntityPage() {
  const { kind, id } = directoryEntityRoute.useParams();
  const navigate = useNavigate();
  const entityId = Number(id);
  const kindOk = KINDS.has(kind);
  const idOk = Number.isFinite(entityId) && entityId > 0;

  const { data, error, isLoading } = useApi(
    directory.resolve,
    kindOk && idOk
      ? { query: { kind: kind as "person" | "organization", id: entityId } }
      : null,
  );

  React.useEffect(() => {
    if (data?.uid) {
      navigate.replace(objectHref(data.uid, "operations"));
    }
  }, [data, navigate]);

  if (!kindOk || !idOk) {
    return (
      <div className="px-6 py-8">
        <PageHeader title="Not found" description="Unknown directory entity." />
      </div>
    );
  }
  if (isLoading && !data) {
    return (
      <div className="px-6 py-8 type-body text-muted-foreground">
        Resolving entity…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="px-6 py-8">
        <PageHeader
          title="Not found"
          description="That person or organization does not exist in this workspace."
        />
      </div>
    );
  }
  return (
    <div className="px-6 py-8 type-body text-muted-foreground">Redirecting…</div>
  );
}

export const directoryEntityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/directory/$kind/$id",
  component: DirectoryEntityPage,
});

// `/integrations/connected` lives in `singletons.tsx` (#44577).
